/**
 * The page's read cache: the real store, the real clock, and the app's own idea of what "no
 * answer" means.
 *
 * Kept apart from `readCache.ts` because this is the part that knows about IndexedDB and the
 * API client, and that file is deliberately free of both.
 */
import { ApiRequestError, refetchForCache } from "../api.ts";
import { currentUserId } from "../identity.ts";
import { IdbCacheStore } from "./idbCacheStore.ts";
import { ReadCache } from "./readCache.ts";
import type { Classification } from "./readCache.ts";

/**
 * - No response at all (offline, DNS, a reset connection) or a 5xx: nobody answered, so a saved
 *   copy is the honest thing to show.
 * - 404 and 410: the server answered and the thing is gone.
 * - Everything else — 401, 403, 422: the server answered *no*, and that is never masked.
 */
export function classifyReadError(error: unknown): Classification {
  if (!(error instanceof ApiRequestError)) return "unreachable";
  if (error.status === 404 || error.status === 410) return "gone";
  return error.status >= 500 ? "unreachable" : "answered";
}

export const readCache = new ReadCache({
  store: new IdbCacheStore(),
  userId: currentUserId,
  classify: classifyReadError,
});

/** Refresh the copies opened most recently. Throttled inside the cache. */
export const refreshReadCache = (): Promise<void> => readCache.refreshRecent(10, refetchForCache);
